import { Logger } from './Logger';
import { LoggingConfig } from './config';
import { correlationManager } from './correlation';

// Create and export the singleton logger instance
export const logger = Logger.getInstance(LoggingConfig.createConfig());

// Export types and utilities
export { correlationManager, CorrelationManager } from './correlation';
export * from './transports';
export * from './types';
export { LoggingConfig };

// Initialize logging system
export function initializeLogging(): void {
  // Set up correlation ID management
  correlationManager.setupFetchInterceptor();

  // Set up global error handlers
  window.addEventListener('error', event => {
    logger.error('Global Error', {
      message: event.error?.message || event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      url: window.location.href,
    });
  });

  window.addEventListener('unhandledrejection', event => {
    logger.error('Unhandled Promise Rejection', {
      reason: event.reason,
      stack: event.reason?.stack,
      url: window.location.href,
    });
  });

  // Log system initialization
  logger.info('Logging system initialized', {
    config: LoggingConfig.getDebugInfo(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  });
}

// Utility functions for common logging patterns
export const logApiError = (error: any, context?: any) => {
  logger.error('API Error', {
    message: error.message,
    status: error.status,
    statusText: error.statusText,
    stack: error.stack,
    ...context,
  });
};

export const logPerformance = (
  operation: string,
  duration: number,
  context?: any
) => {
  const level = duration > 1000 ? 'warn' : 'info';
  logger[level](`Performance: ${operation}`, {
    operation,
    duration,
    slow: duration > 1000,
    ...context,
  });
};

export const logUserAction = (action: string, context?: any) => {
  logger.info(`User Action: ${action}`, {
    action,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    ...context,
  });
};

// Debug utilities (only available in development)
if (import.meta.env.DEV) {
  (window as any).__spa_ui_logger__ = {
    logger,
    correlationManager,
    config: LoggingConfig.getDebugInfo(),
    downloadLogs: async () => {
      const devTransport = logger['config'].transports.find(
        t => t.constructor.name === 'DevFileTransport'
      );
      if (devTransport && 'downloadLogs' in devTransport) {
        await (devTransport as any).downloadLogs();
      }
    },
    clearLogs: async () => {
      const devTransport = logger['config'].transports.find(
        t => t.constructor.name === 'DevFileTransport'
      );
      if (devTransport && 'clearLogs' in devTransport) {
        await (devTransport as any).clearLogs();
      }
    },
  };
}
